import type { CoverageEntry } from '../api/masterCertificateApi';

// ── Form data shape exposed via forwardRef ────────────────────────────────────

export interface Acord25FormData {
  // Producer / contact
  producerAddress: string;
  producerCity: string;
  producerStreet: string;
  producerState: string;
  producerZip: string;
  contactName: string;
  contactPhone: string;
  contactFax: string;
  contactEmail: string;
  // Insured
  insuredName: string;
  insuredAddress: string;
  insuredStreet: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
  // Insurers A–F (names + carrier databaseIds for NaicId)
  insurerA: string; insurerB: string; insurerC: string;
  insurerD: string; insurerE: string; insurerF: string;
  insurerAId: string | null; insurerBId: string | null; insurerCId: string | null;
  insurerDId: string | null; insurerEId: string | null; insurerFId: string | null;
  // General Liability (CGL)
  cglInsurerLtr: string;
  cglPolicyNumber: string;
  cglEffDate: Date | null;
  cglExpDate: Date | null;
  cglClaimsMade: boolean;
  cglOccur: boolean;
  cglBlank1Check: boolean; cglBlank1Text: string;
  cglBlank2Check: boolean; cglBlank2Text: string;
  cglPolicy: boolean;
  cglProject: boolean;
  cglLoc: boolean;
  cglOther: boolean;
  cglOtherText: string;
  cglEachOccurrence: string;
  cglDamageRented: string;
  cglMedExp: string;
  cglPersonalAdv: string;
  cglGeneralAgg: string;
  cglProductsAgg: string;
  cglBlankLimitLabel: string; cglBlankLimitAmt: string;
  cglAddlInsd: boolean;
  cglSbrWvd: boolean;
  // Auto Liability
  autoInsurerLtr: string;
  autoPolicyNumber: string;
  autoEffDate: Date | null;
  autoExpDate: Date | null;
  autoAnyAuto: boolean;
  autoAllOwned: boolean;
  autoScheduled: boolean;
  autoHired: boolean;
  autoNonOwned: boolean;
  autoBlank1Check: boolean; autoBlank1Text: string;
  autoBlank2Check: boolean; autoBlank2Text: string;
  autoCombinedSingle: string;
  autoBodilyPerson: string;
  autoBodilyAccident: string;
  autoPropertyDamage: string;
  autoBlankLimitLabel: string; autoBlankLimitAmt: string;
  autoAddlInsd: boolean;
  autoSbrWvd: boolean;
  // Umbrella / Excess Liability
  umbrellaInsurerLtr: string;
  umbrellaPolicyNumber: string;
  umbrellaEffDate: Date | null;
  umbrellaExpDate: Date | null;
  umbrellaLiab: boolean;
  excessLiab: boolean;
  umbrellaOccur: boolean;
  umbrellaClaimsMade: boolean;
  umbrellaDed: boolean;
  umbrellaRetentionCheck: boolean;
  umbrellaRetention: string;
  umbrellaEachOccurrence: string;
  umbrellaAggregate: string;
  umbrellaBlankLimitLabel: string; umbrellaBlankLimitAmt: string;
  umbrellaAddlInsd: boolean;
  umbrellaSbrWvd: boolean;
  // Workers Compensation
  wcInsurerLtr: string;
  wcPolicyNumber: string;
  wcEffDate: Date | null;
  wcExpDate: Date | null;
  wcPerStatute: boolean;
  wcOther: boolean;
  wcOtherText: string;
  wcEachAccident: string;
  wcDiseaseEmployee: string;
  wcDiseasePolicyLimit: string;
  wcExcluded: boolean;
  wcSpecialProvisions: string;
  wcAddlInsd: boolean;
  wcSbrWvd: boolean;
  // Other Coverage 1
  other1InsurerLtr: string;
  other1Type: string;
  other1PolicyNumber: string;
  other1EffDate: Date | null;
  other1ExpDate: Date | null;
  other1LimitLabel1: string;
  other1LimitAmt1: string;
  other1LimitLabel2: string;
  other1LimitAmt2: string;
  other1LimitLabel3: string;
  other1LimitAmt3: string;
  other1AddlInsd: boolean;
  other1SbrWvd: boolean;
  // Other Coverage 2 (legacy — not shown in UI)
  other2InsurerLtr: string;
  other2Type: string;
  other2PolicyNumber: string;
  other2EffDate: Date | null;
  other2ExpDate: Date | null;
  other2Limits: string;
  // Misc / Footer
  certNumber: string;
  revisionNumber: string;
  descriptionOfOps: string;
  holderName: string;
  holderAddress: string;
  holderCity: string;
  holderStreet: string;
  holderState: string;
  holderZip: string;
  authRepSignature: string; // SignatureId GUID (or empty)
  certDate: Date | null;
  // ACORD 101
  showAcord101: boolean;
  acord101Agency: string;
  acord101NamedInsured: string;
  acord101AdditionalRemarks: string;
}

// ── Date helpers ──────────────────────────────────────────────────────────────

function toPayloadDate(d: Date | null): string {
  if (!d) return '';
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy} 00:00:00`;
}

function toXmlDate(d: Date | null): string {
  if (!d) return '';
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${mo}/${dy}/${d.getFullYear()}`;
}

// ── XML helpers ───────────────────────────────────────────────────────────────

const yn = (v: boolean) => (v ? 'Yes' : 'No');

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Section XML builders ──────────────────────────────────────────────────────

function buildGlXml(d: Acord25FormData): string {
  return (
    `<Coverages><GeneralLiability>` +
    `<Date>${esc(toXmlDate(d.certDate))}</Date>` +
    `<Acord25ProducerFullName>${esc(d.producerAddress)}</Acord25ProducerFullName>` +
    `<Acord25ProducerAddressLineOne>${esc(d.producerStreet)}</Acord25ProducerAddressLineOne>` +
    `<Acord25ProducerAddressLineTwo></Acord25ProducerAddressLineTwo>` +
    `<Acord25ProducerCityName>${esc(d.producerCity)}</Acord25ProducerCityName>` +
    `<Acord25ProducerState>${esc(d.producerState)}</Acord25ProducerState>` +
    `<Acord25ProducerPostalCode>${esc(d.producerZip)}</Acord25ProducerPostalCode>` +
    `<Acord25NamedInsuredFullName>${esc(d.insuredName)}</Acord25NamedInsuredFullName>` +
    `<Acord25NamedInsuredAddressLineOne>${esc(d.insuredAddress)}</Acord25NamedInsuredAddressLineOne>` +
    `<Acord25NamedInsuredAddressLineTwo>${esc(d.insuredStreet)}</Acord25NamedInsuredAddressLineTwo>` +
    `<Acord25NamedInsuredCityName>${esc(d.insuredCity)}</Acord25NamedInsuredCityName>` +
    `<Acord25NamedInsuredPostalCode>${esc(d.insuredZip)}</Acord25NamedInsuredPostalCode>` +
    `<Acord25NamedInsuredState>${esc(d.insuredState)}</Acord25NamedInsuredState>` +
    `<CommercialGeneralLiability>${d.cglPolicyNumber ? 'Yes' : 'No'}</CommercialGeneralLiability>` +
    `<ClaimsMade>${yn(d.cglClaimsMade)}</ClaimsMade>` +
    `<Occur>${yn(d.cglOccur)}</Occur>` +
    `<Policy>${yn(d.cglPolicy)}</Policy>` +
    `<Project>${yn(d.cglProject)}</Project>` +
    `<Loc>${yn(d.cglLoc)}</Loc>` +
    `<Other text="${esc(d.cglBlank1Text)}">${yn(d.cglBlank1Check)}</Other>` +
    `<Other2 text="${esc(d.cglBlank2Text)}">${yn(d.cglBlank2Check)}</Other2>` +
    `<Other2016 text="">No</Other2016>` +
    `<OtherAggregate text="${esc(d.cglOtherText)}">${yn(d.cglOther)}</OtherAggregate>` +
    `<PhoneNumber>${esc(d.contactPhone)}</PhoneNumber>` +
    `<Fax>${esc(d.contactFax)}</Fax>` +
    `<EmailAddresss>${esc(d.contactEmail)}</EmailAddresss>` +
    `<ContactName>${esc(d.contactName)}</ContactName>` +
    `<CertificateNumber>${esc(d.certNumber)}</CertificateNumber>` +
    `<RevisionNumber>${esc(d.revisionNumber)}</RevisionNumber>` +
    `<DescriptionOfOperations>${esc(d.descriptionOfOps)}</DescriptionOfOperations>` +
    `</GeneralLiability></Coverages>` +
    `<Limits><GeneralLiability>` +
    `<EachOccurrence>${esc(d.cglEachOccurrence)}</EachOccurrence>` +
    `<Damage>${esc(d.cglDamageRented)}</Damage>` +
    `<MedExp>${esc(d.cglMedExp)}</MedExp>` +
    `<Injury>${esc(d.cglPersonalAdv)}</Injury>` +
    `<Aggregate>${esc(d.cglGeneralAgg)}</Aggregate>` +
    `<Products>${esc(d.cglProductsAgg)}</Products>` +
    `<Other text="${esc(d.cglBlankLimitLabel)}">${esc(d.cglBlankLimitAmt)}</Other>` +
    `</GeneralLiability></Limits>`
  );
}

function buildAutoXml(d: Acord25FormData): string {
  return (
    `<Coverages><AutomobileLiability>` +
    `<AnyAuto>${yn(d.autoAnyAuto)}</AnyAuto>` +
    `<AllOwnedAutos>${yn(d.autoAllOwned)}</AllOwnedAutos>` +
    `<ScheduledAutos>${yn(d.autoScheduled)}</ScheduledAutos>` +
    `<HiredAutos>${yn(d.autoHired)}</HiredAutos>` +
    `<NonOwnedAutos>${yn(d.autoNonOwned)}</NonOwnedAutos>` +
    `<Other text="${esc(d.autoBlank1Text)}">${yn(d.autoBlank1Check)}</Other>` +
    `<Other2 text="${esc(d.autoBlank2Text)}">${yn(d.autoBlank2Check)}</Other2>` +
    `</AutomobileLiability></Coverages>` +
    `<Limits><AutomobileLiability>` +
    `<CombinedSingle>${esc(d.autoCombinedSingle)}</CombinedSingle>` +
    `<BodilyInjuryPerPerson>${esc(d.autoBodilyPerson)}</BodilyInjuryPerPerson>` +
    `<BodilyInjuryPerAccident>${esc(d.autoBodilyAccident)}</BodilyInjuryPerAccident>` +
    `<PropertyDamage>${esc(d.autoPropertyDamage)}</PropertyDamage>` +
    `<Other text="${esc(d.autoBlankLimitLabel)}">${esc(d.autoBlankLimitAmt)}</Other>` +
    `</AutomobileLiability></Limits>`
  );
}

function buildUmbrellaXml(d: Acord25FormData): string {
  return (
    `<Coverages><UmbrellaLiability>` +
    `<Umbrella>${yn(d.umbrellaLiab)}</Umbrella>` +
    `<Excess>${yn(d.excessLiab)}</Excess>` +
    `<Occur>${yn(d.umbrellaOccur)}</Occur>` +
    `<ClaimsMade>${yn(d.umbrellaClaimsMade)}</ClaimsMade>` +
    `<Ded>${yn(d.umbrellaDed)}</Ded>` +
    `<Retention text="${esc(d.umbrellaRetention)}">${yn(d.umbrellaRetentionCheck)}</Retention>` +
    `</UmbrellaLiability></Coverages>` +
    `<Limits><UmbrellaLiability>` +
    `<EachOccurrence>${esc(d.umbrellaEachOccurrence)}</EachOccurrence>` +
    `<Aggregate>${esc(d.umbrellaAggregate)}</Aggregate>` +
    `<Other text="${esc(d.umbrellaBlankLimitLabel)}">${esc(d.umbrellaBlankLimitAmt)}</Other>` +
    `</UmbrellaLiability></Limits>`
  );
}

function buildWcXml(d: Acord25FormData): string {
  return (
    `<Coverages><EmployerLiability>` +
    `<Excluded text="${esc(d.descriptionOfOps)}">${esc(d.wcSpecialProvisions)}</Excluded>` +
    `</EmployerLiability></Coverages>` +
    `<Limits><EmployerLiability>` +
    `<WcStatutory>${yn(d.wcPerStatute)}</WcStatutory>` +
    `<Other text="${yn(d.wcOther)}">${esc(d.wcOtherText)}</Other>` +
    `<EachAccident>${esc(d.wcEachAccident)}</EachAccident>` +
    `<Employee>${esc(d.wcDiseaseEmployee)}</Employee>` +
    `<Policy>${esc(d.wcDiseasePolicyLimit)}</Policy>` +
    `</EmployerLiability></Limits>`
  );
}

function buildOther1Xml(d: Acord25FormData): string {
  return (
    `<Coverages><OtherLiability>` +
    `<Text>${esc(d.other1Type)}</Text>` +
    `</OtherLiability></Coverages>` +
    `<Limits><OtherLiability>` +
    `<CoverageCode>${esc(d.other1LimitLabel1)}</CoverageCode>` +
    `<CoverageCode1>${esc(d.other1LimitLabel2)}</CoverageCode1>` +
    `<CoverageCode2>${esc(d.other1LimitLabel3)}</CoverageCode2>` +
    `<Text>${esc(d.other1LimitAmt1)}</Text>` +
    `<Text1>${esc(d.other1LimitAmt2)}</Text1>` +
    `<Text2>${esc(d.other1LimitAmt3)}</Text2>` +
    `</OtherLiability></Limits>`
  );
}

function buildOther2Xml(type: string, limits: string): string {
  return (
    `<Coverages><Other2Liability>` +
    `<Text>${esc(type)}</Text>` +
    `</Other2Liability></Coverages>` +
    `<Limits><Other2Liability>` +
    `<Text>${esc(limits)}</Text>` +
    `</Other2Liability></Limits>`
  );
}

function buildCertHolderXml(d: Acord25FormData): string {
  return (
    `<Coverages><CertificateHolderInformationCoverage>` +
    `<CertificateHolder>` +
    `<Name>${esc(d.holderName)}</Name>` +
    `<Address>${esc(d.holderAddress)}</Address>` +
    `<City>${esc(d.holderCity)}</City>` +
    `<Street>${esc(d.holderStreet)}</Street>` +
    `<State>${esc(d.holderState)}</State>` +
    `<Zip>${esc(d.holderZip)}</Zip>` +
    `</CertificateHolder>` +
    `</CertificateHolderInformationCoverage></Coverages>`
  );
}

function buildAcord101Xml(d: Acord25FormData): string {
  return (
    `<Coverages><Acord101AdditionalRemarks>` +
    `<RemarksText>${esc(d.acord101AdditionalRemarks)}</RemarksText>` +
    `</Acord101AdditionalRemarks></Coverages><Limits />`
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Builds the `Coverages` array for the create-master-certificate payload
 * from Acord25 form state. One entry is emitted per coverage section that
 * has a policy number filled in.
 */
export function buildAcord25Coverages(d: Acord25FormData, acordFormNumber = 2516): CoverageEntry[] {
  const entries: CoverageEntry[] = [];
  const sig = d.authRepSignature || null;
  const base = acordFormNumber;

  const carrierIdByLetter = (letter: string): string | null => {
    if (letter === 'A') return d.insurerAId ?? null;
    if (letter === 'B') return d.insurerBId ?? null;
    if (letter === 'C') return d.insurerCId ?? null;
    if (letter === 'D') return d.insurerDId ?? null;
    if (letter === 'E') return d.insurerEId ?? null;
    if (letter === 'F') return d.insurerFId ?? null;
    return null;
  };

  entries.push({
    PolicyNumber: d.cglPolicyNumber ?? '',
    NaicId: carrierIdByLetter(d.cglInsurerLtr),
    EffectiveDate: toPayloadDate(d.cglEffDate),
    ExpirationDate: toPayloadDate(d.cglExpDate),
    AcordFormNumber: base,
    CoveragesAndLimits: buildGlXml(d),
    SignatureId: sig,
  });

  if (d.autoPolicyNumber) {
    entries.push({
      PolicyNumber: d.autoPolicyNumber,
      NaicId: carrierIdByLetter(d.autoInsurerLtr),
      EffectiveDate: toPayloadDate(d.autoEffDate),
      ExpirationDate: toPayloadDate(d.autoExpDate),
      AcordFormNumber: base,
      CoveragesAndLimits: buildAutoXml(d),
      SignatureId: sig,
    });
  }

  if (d.umbrellaPolicyNumber) {
    entries.push({
      PolicyNumber: d.umbrellaPolicyNumber,
      NaicId: carrierIdByLetter(d.umbrellaInsurerLtr),
      EffectiveDate: toPayloadDate(d.umbrellaEffDate),
      ExpirationDate: toPayloadDate(d.umbrellaExpDate),
      AcordFormNumber: base,
      CoveragesAndLimits: buildUmbrellaXml(d),
      SignatureId: sig,
    });
  }

  entries.push({
    PolicyNumber: d.wcPolicyNumber ?? '',
    NaicId: carrierIdByLetter(d.wcInsurerLtr),
    EffectiveDate: toPayloadDate(d.wcEffDate),
    ExpirationDate: toPayloadDate(d.wcExpDate),
    AcordFormNumber: base,
    CoveragesAndLimits: buildWcXml(d),
    SignatureId: sig,
  });

  if (d.other1PolicyNumber) {
    entries.push({
      PolicyNumber: d.other1PolicyNumber,
      NaicId: carrierIdByLetter(d.other1InsurerLtr),
      EffectiveDate: toPayloadDate(d.other1EffDate),
      ExpirationDate: toPayloadDate(d.other1ExpDate),
      AcordFormNumber: base,
      CoveragesAndLimits: buildOther1Xml(d),
      SignatureId: sig,
    });
  }

  if (d.other2PolicyNumber) {
    entries.push({
      PolicyNumber: d.other2PolicyNumber,
      NaicId: carrierIdByLetter(d.other2InsurerLtr),
      EffectiveDate: toPayloadDate(d.other2EffDate),
      ExpirationDate: toPayloadDate(d.other2ExpDate),
      AcordFormNumber: base,
      CoveragesAndLimits: buildOther2Xml(d.other2Type, d.other2Limits),
      SignatureId: sig,
    });
  }

  if (d.showAcord101) {
    entries.push({
      PolicyNumber: '',
      NaicId: null,
      EffectiveDate: '',
      ExpirationDate: '',
      AcordFormNumber: 101,
      CoveragesAndLimits: buildAcord101Xml(d),
      SignatureId: sig,
    });
  }

  if (d.holderName) {
    entries.push({
      PolicyNumber: '',
      NaicId: null,
      EffectiveDate: '',
      ExpirationDate: '',
      AcordFormNumber: 2716,
      CoveragesAndLimits: buildCertHolderXml(d),
      SignatureId: sig,
    });
  }

  return entries;
}
